import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";

import { IrtRows } from "./Irt";
import type { UptimeRow } from "./api";
import { flagWord, issueCount, marketplaceLabel, parisDateTime, storeLabel, type ShownVerdict } from "./format";

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 py-1 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right text-ks-ink">{value}</dd>
    </div>
  );
}

/**
 * The result detail opens in a shadcn Sheet (the StockDetail pattern): the raw feed flags behind the status word,
 * so an operator can see why a row reads as it does (REQ-UPT-004: a failed check names its reason).
 */
export function UptimeDetail({ row, shown, createFailed, onClose }: { row: UptimeRow | null; shown?: ShownVerdict; createFailed?: boolean; onClose: () => void }) {
  return (
    <Sheet open={row !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      {row ? (
        <SheetContent side="right" className="overflow-y-auto data-[side=right]:w-full data-[side=right]:sm:max-w-[560px]" onCloseAutoFocus={(event) => event.preventDefault()}>
          <SheetHeader>
            <SheetTitle>{row.sku} · {marketplaceLabel(row)}</SheetTitle>
            <SheetDescription>{row.name}{row.asin ? ` · ${row.asin}` : ""} · {storeLabel(row)}</SheetDescription>
          </SheetHeader>
          <Detail row={row} shown={shown} createFailed={createFailed} />
        </SheetContent>
      ) : null}
    </Sheet>
  );
}

function Detail({ row, shown, createFailed }: { row: UptimeRow; shown?: ShownVerdict; createFailed?: boolean }) {
  const issues = issueCount(row.issues_json);
  return (
    <div className="px-4 pb-4">
        <dl>
          <Row label="Status" value={row.status} />
          <Row label="Reason" value={row.reason ?? "none: the listing is buyable"} />
          <Row label="Buyable" value={flagWord(row.buyable)} />
          <Row label="Discoverable" value={flagWord(row.discoverable)} />
          <Row label="Listing issues" value={issues ?? "none carried"} />
          <Row label="Listing read at" value={parisDateTime(row.read_at) ?? "no listing row in the feed"} />
          <Row label="Amazon SKU" value={row.amazon_sku ?? "not set"} />
          <Row label="Entity" value={row.entity_id} />
        </dl>
        {/* C2 v1.7: the verdict the row paints: its own, or the stock-out's behind an out-of-stock listing (D7 there). */}
        <IrtRows verdict={shown?.verdict} via={shown?.via} createFailed={createFailed} />
    </div>
  );
}
